import * as React from 'react';

import { CatalyticModalMode } from '@rematter/modules/trade/_state/loadStaged.state';
import { InventoryItemRecord } from '@rematter/modules/trade/types/load';
import {
  cn,
  MaterialSymbol,
  pascalToSentenceCase,
  Tab,
  TextField,
  VirtualKeyboardWrapper,
} from '@rematter/ui-web';
import { LucideCarFront, LucideCircleX, LucideSearch } from 'lucide-react';

import { CatalyticItemTab } from './CatalyticConverterDetailView';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

export type CatalyticConverterModalHeaderProps = {
  /** Explicit modal mode defining user intent */
  mode: CatalyticModalMode;
  /** True when showing detail view (edit mode) */
  isDetailView: boolean;
  searchInputRef: React.Ref<HTMLDivElement>;
  selectedItem?: InventoryItemRecord;
  activeTab: CatalyticItemTab;
  onTabChange: (tab: CatalyticItemTab) => void;
  searchTerm: string;
  onChangeInput: (value: string) => void;
  clearSearchTerm: VoidFunction;
  autoFocus?: boolean;
  /** Whether touch mode is enabled (shows virtual keyboard) */
  touchModeEnabled?: boolean;
};

const CATALYTIC_TABS: CatalyticItemTab[] = [
  'vehicle',
  'notes',
  'photos',
  'files',
];

const MaterialMiniCard: React.FC<{ item?: InventoryItemRecord }> = ({
  item,
}) => (
  <div
    className={cn('@MaterialMiniCard', 'h-[50px] rounded-md bg-white p-1 pr-2')}
  >
    <div className="flex h-full items-stretch gap-2">
      <MaterialSymbol
        symbolIcon={<LucideCarFront size={16} />}
        className="h-[40px] w-[40px] bg-primary/20 text-primary"
      />
      <div className="flex w-full flex-col justify-center gap-0.5 pb-0.5">
        <div className="text-[11px] leading-[15px] font-normal text-neutral/75">
          {pascalToSentenceCase(item?.category?.name ?? '')}
        </div>

        <div className="text-[15px] leading-[15px] font-medium">
          {item?.name}
        </div>
      </div>
    </div>
  </div>
);

export const CatalyticConverterModalHeader = ({
  mode,
  isDetailView,
  searchInputRef,
  selectedItem,
  activeTab,
  onTabChange,
  searchTerm,
  onChangeInput,
  clearSearchTerm,
  autoFocus = false,
  touchModeEnabled = false,
}: CatalyticConverterModalHeaderProps) => {
  const t = useTranslations();

  const tabLabels: Record<CatalyticItemTab, string> = {
    vehicle: t.inventoryItemsModal.catalyticTabs.vehicle,
    notes: t.inventoryItemsModal.catalyticTabs.notes,
    photos: t.inventoryItemsModal.catalyticTabs.photos,
    files: t.inventoryItemsModal.catalyticTabs.files,
  };

  const getTitle = () => {
    if (mode === 'change') {
      return t.inventoryItemsModal.selectCatalyticConverter;
    }
    return t.inventoryItemsModal.addCatalyticConverters;
  };

  return (
    <div
      className={cn(
        '@CatalyticConverterModalHeader',
        'm-0 flex w-full items-center justify-between gap-5',
      )}
    >
      {isDetailView ? (
        <>
          <MaterialMiniCard item={selectedItem} />
          <div className="flex w-full flex-1 flex-shrink-0 items-center justify-center gap-1">
            {CATALYTIC_TABS.map((tab) => (
              <Tab
                key={tab}
                selected={activeTab === tab}
                onClick={() => onTabChange(tab)}
                variant="primary"
                className={cn(
                  'w-[82px] rounded-md border-none px-4 py-4.5 text-center text-base',
                  activeTab === tab
                    ? '!border-none !bg-primary/20 !text-primary'
                    : 'text-neutral hover:!bg-neutral/20',
                )}
              >
                {tabLabels[tab]}
              </Tab>
            ))}
          </div>
        </>
      ) : (
        <>
          <span className="pl-3 text-lg font-semibold">{getTitle()}</span>
          <div className="relative h-full">
            <VirtualKeyboardWrapper
              value={searchTerm}
              onChange={onChangeInput}
              disabled={!touchModeEnabled}
            >
              <TextField
                ref={searchInputRef}
                prefix={<LucideSearch size={20} />}
                suffix={
                  searchTerm.length > 0 && (
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 flex cursor-pointer items-center justify-center bg-transparent p-0 text-neutral"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={clearSearchTerm}
                    >
                      <LucideCircleX size={20} className="opacity-60" />
                    </button>
                  )
                }
                inputMode="none"
                className="h-full w-[350px] rounded-md bg-white placeholder:text-neutral focus:border-primary focus:ring-2 disabled:opacity-50"
                placeholder={t.inventoryItemsModal.searchPlaceholder}
                autoFocus={autoFocus}
              />
            </VirtualKeyboardWrapper>
          </div>
        </>
      )}
      <div className="flex gap-3">{/* Placeholder for action buttons */}</div>
    </div>
  );
};
